import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { set } from 'idb-keyval';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useRouteMatch } from 'react-router-dom';

import { Helmet } from 'react-helmet';
import {
  Constants,
  GLOBAL_TOGGLE_KEY,
  USER_ROLES,
  getGlobalToggleState,
  userTokenService,
} from '@pwc/pwc-deals-web-common';
import { useEventSubscription, useEventHub } from '@pwc/event-hub-ui';
import { CommentFreezeArea } from '@pwc/pwc-deals-ui';
import { getContentColourConversion } from 'features/project/projectSlice';

import { isCustomColourConversionActive } from 'common/helpers/projectInitUtil';
import Footer from '../common/components/Footer/Footer';
import Header from '../common/components/Header/Header';
import SideNav from '../common/components/SideNav/SideNav';
import Pendo from '../features/userAnalytics/Pendo';
import ContentRouter from './ContentRouter';
import { CookiesBanner } from './CookiesBanner/CookiesBanner';

import UnauthenticatedRoute from './UnauthenticatedRoute';
import { getMobileSidebarOpened } from '../features/appSettings/appSettingsSlice';
import { getCommentPanelOpened } from '../features/comments/commentsSlice';
import { getUserSettingsSelector } from '../common/redux/userSettings/userSettingsSlice';
import { loadUserSettingsThunk } from '../common/redux/userSettings/userSettingsThunk';
import authorizationService, {
  CLIENT_TOKEN_KEY,
  PWC_USER_TOKEN_KEY,
} from '../common/services/security/authorizationService';
import ModalError from '../common/components/ModalError/ModalError';
import './AppLayout.scss';
import { getMuiTheme } from '../mui/mui-theme.ts';
import GlobalError from '../common/components/GlobalError';
import { handleEvents } from '../common/events-handlers/index.ts';
import storage from '../common/helpers/storage';
import {
  getActualUserRole,
  getUserRole,
  setActualUserRole,
  setUserRole,
} from '../common/redux/security/loggedInUser/loggedInUserSlice';
import { GlobalToggleOutline } from '../common/components/GlobalToggleOutline';
import { umMfeFn } from '../features/userManagement/userManagementUtil';
import featureFlags from '../common/helpers/featureFlags';
import { getPlaintextToken } from '../common/services/security/userTokenService';
import LanguageSelectionModal from '../common/components/LanguageSelection/LanguageSelectionModal';

const INDEXDB_PROJECT_TOKEN_KEY = 'projectToken';

const useHideFooterForRoute = () => {
  const matchForSA = useRouteMatch([
    '/projects/:projectId/template-analyses/:analysisId/edit',
    '/projects/:projectId/template-analyses/:analysisId/view/:version',
  ]);
  return matchForSA?.isExact ?? false;
};

const AppLayout = () => {
  const { getProjectToken } = userTokenService;

  let themeClass = storage.get('pwc-theme');
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { eventHub } = useEventHub();
  const userSettings = useSelector(getUserSettingsSelector);
  const { isLanguageSelectionEnabled } = featureFlags;

  const contentColourConversion = useSelector(getContentColourConversion);
  const isCustomColourConversion = isCustomColourConversionActive(
    contentColourConversion
  );

  if (userSettings.settings.darkMode !== undefined) {
    if (userSettings.settings.darkMode) {
      themeClass = 'theme-dark';
    } else if (isCustomColourConversion) {
      themeClass = 'theme-light';
    } else {
      themeClass = 'theme-light enable-pwc-colour-conversion';
    }
  }

  // The below logic is used when the theme changes. Junction is changing its theme first and then UM-MFE is changing.
  // Before junction changes, the below code will unmount the UM-MFE so it doesnt have any user experience issue.
  const [oldTheme, setOldTheme] = useState(themeClass);

  if (themeClass !== oldTheme) {
    if (window.location.pathname.includes('team-members')) {
      if (umMfeFn.cleanUpFunc) {
        umMfeFn.cleanUpFunc('um-mfe-node');
      }
    }
  }

  const hasValidToken = authorizationService.hasValidToken();
  useEffect(() => {
    if (hasValidToken && !userSettings.loaded && !userSettings.loading) {
      dispatch(loadUserSettingsThunk());
    }
  }, [hasValidToken, userSettings.loaded, userSettings.loading, dispatch]);

  const projectId = useSelector((state) => state.project && state.project.id);
  const mobileSidebarOpened = useSelector(getMobileSidebarOpened);
  const commentPanelOpened = useSelector(getCommentPanelOpened);
  const freezeClass = mobileSidebarOpened || commentPanelOpened ? 'freeze' : '';

  const hideFooterForRoute = useHideFooterForRoute();
  useEffect(() => {
    const htmlElement = document.getElementsByTagName('html')[0];

    if (mobileSidebarOpened) htmlElement.classList.add('mobile-sidenav-open');
    else htmlElement.classList.remove('mobile-sidenav-open');
  }, [mobileSidebarOpened]);

  useEffect(() => {
    eventHub.publish('theme-change', {
      darkMode: userSettings.settings.darkMode,
    });
    setOldTheme(themeClass);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettings?.settings?.darkMode]);

  useEventSubscription('host-sync', (payload) => {
    handleEvents(payload, { dispatch });
  });

  useEffect(() => {
    if (!projectId || !getProjectToken) {
      return;
    }

    const projectToken = getProjectToken(projectId);
    set(INDEXDB_PROJECT_TOKEN_KEY, projectToken);
  }, [projectId, getProjectToken]);

  const userRole = useSelector(getUserRole);
  const actualPwcUser = useSelector(getActualUserRole) === USER_ROLES.PWC_USER;

  useEffect(() => {
    if (projectId && getProjectToken) {
      const projectToken = getPlaintextToken(projectId, userRole);

      if (
        projectToken?.role?.toLowerCase() ===
        Constants.USER_ROLES.THIRD_PARTY_MEMBER.toLowerCase()
      ) {
        dispatch(setUserRole(USER_ROLES.THIRD_PARTY));
        dispatch(setActualUserRole(USER_ROLES.THIRD_PARTY));
      }
    }
  }, [projectId, getProjectToken, userRole, dispatch]);

  // Update the theme only if the mode changes
  const muiTheme = useMemo(
    () =>
      createTheme(
        getMuiTheme(userSettings.settings.darkMode === false ? 'light' : 'dark')
      ),
    [userSettings.settings.darkMode]
  );

  useEffect(() => {
    if (!featureFlags.isGlobalToggleEnabled) {
      return;
    }
    const globalToggleSession = getGlobalToggleState();
    if (!actualPwcUser || !globalToggleSession) {
      return;
    }
    const sessionStorageKeys = Object.keys(sessionStorage);
    const isProjectToken = (key) => key.indexOf('project') === 0;

    if (userRole === USER_ROLES.CLIENT || userRole === USER_ROLES.THIRD_PARTY) {
      sessionStorageKeys.forEach((key) => {
        if (isProjectToken(key)) {
          const clientToken = sessionStorage.getItem(
            `${CLIENT_TOKEN_KEY}${key}`
          );
          sessionStorage.setItem(key, clientToken);
        }
      });
    } else if (userRole === USER_ROLES.PWC_USER) {
      sessionStorageKeys.forEach((key) => {
        if (isProjectToken(key)) {
          const pwcUserToken = sessionStorage.getItem(
            `${PWC_USER_TOKEN_KEY}${key}`
          );
          sessionStorage.setItem(key, pwcUserToken);
        }
      });
    }
  }, [userRole, actualPwcUser]);

  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    const handleGlobalToggleChange = () => {
      /**
       * The forced updated is needed in order to reload
       * all components where UserUtilities.isUserInternal()
       * function is used.
       */
      forceUpdate();
    };

    window.addEventListener(GLOBAL_TOGGLE_KEY, handleGlobalToggleChange);

    return () => {
      window.removeEventListener(GLOBAL_TOGGLE_KEY, handleGlobalToggleChange);
    };
  }, []);

  if (!userRole) {
    return null;
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <div className={`app-layout ${freezeClass}`}>
        {isLanguageSelectionEnabled ? <LanguageSelectionModal /> : null}
        <Helmet>
          <body className={`no-outline ${themeClass}`} />
        </Helmet>
        <Pendo />

        {featureFlags.isGlobalToggleEnabled && getGlobalToggleState() && (
          <GlobalToggleOutline />
        )}
        <SideNav />
        <main>
          <Header projectId={projectId} />
          <div id="main-content" className="sr-only">
            Main Content
          </div>
          {pathname.toLowerCase() !== '/unauthenticated' && <ContentRouter />}
          {pathname.toLowerCase() === '/unauthenticated' && (
            <UnauthenticatedRoute />
          )}
          {!hideFooterForRoute && <Footer />}
          <CookiesBanner />
        </main>
        <GlobalError />
        <ModalError />
        <CommentFreezeArea />
      </div>
    </ThemeProvider>
  );
};
export default AppLayout;
